import AppLoader from '@nxsys/components/AppLoader';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  paymentModels,
  pensionDeductionMethods,
} from '@nxsys/constants/AppConstants';
import { isCISPaymentModel } from '@nxsys/helpers/Common';
import { useDownloadPayslipEmployee } from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetPayslipEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Icons } from '@nxsys/types/icons';
import { IPayslip } from '@nxsys/types/models/payslip';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import {
  getShownEmployeePension,
  getShownEmployersPension,
} from '@nxsys/utils/reported-pension';
import { Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce, includes } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  employeeId: string;
};

/**
 * Ireland payrolls leave `payeeUmbrellaValue` empty and don't populate the
 * `totalDeduction` roll-up, so fall back to summing the statutory deductions
 * held on `irelandValue`.
 */
const getIrelandTotalDeduction = (payrollRef: any): number => {
  if (payrollRef?.totalDeduction) return payrollRef.totalDeduction;
  if (payrollRef?._totalDeduction) return payrollRef._totalDeduction;
  const irl = payrollRef?.irelandValue;
  return (
    (irl?.tax || 0) +
    (irl?.usc || 0) +
    (irl?.eePRSI || 0) +
    (irl?.eePension || 0) +
    (irl?.lptDeducted || 0)
  );
};

const Payslip: React.FC<Props> = ({ employeeId }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const currentTaxWeekTaxYear = getTaxWeekTaxYear();

  const { payrollCountry, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const paymentModel =
    user?.subcontractorId?.setting?.paymentModel ||
    user?.subcontractorId?.paymentModel;

  const isCIS = isCISPaymentModel(paymentModel);

  const { payslipList } = useSelector((state: AppState) => state.employeeStore);
  const { loading, list, pagination, queryParams } = payslipList;

  const { run: downloadPayslipEmployee, loading: isDownloadPayslipPending } =
    useDownloadPayslipEmployee();

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxWeekTaxYear.taxYear,
    });
  }, []);

  const handleFetchData = (params: GetPayslipEmployeeListParams) => {
    const newParams = { ...params, id: employeeId };
    dispatch(employeeActions.getPayslipEmployeeList(newParams));
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const defaultColumns: ColumnsType<IPayslip> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => handleDownloadPayslip(record.id)}>
            <Tooltip title="Download">
              <span>
                <Icons.IconDownload />
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'id',
      title: messages['common.payslipsId'],
      dataIndex: 'id',
      render: (value) => value?.replace('SFB', 'PAY') || '_',
    },
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'],
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        `${value?.taxPeriod || ''}/${
          value?.taxYear || value?.currentYear || ''
        }`,
    },
    {
      key: 'paymentModel',
      title: messages['common.paymentModel'],
      dataIndex: 'payrollRef',
      render: (values) =>
        values?.paymentModel
          ? paymentModels[values.paymentModel as keyof typeof paymentModels]
          : '_',
    },
    {
      key: 'paymentCompany',
      title: messages['common.paymentCompany'],
      dataIndex: 'companyRef',
      width: 350,
      render: (values) => values?.companyName || '_',
    },
    ...(!isBureau
      ? ([
          {
            key: 'agencyName',
            title: messages['common.agencyName'],
            dataIndex: 'payrollRef',
            width: 350,
            render: (values: any) => values?.agencyName || '_',
          },
        ] as ColumnsType<IPayslip>)
      : []),
    {
      key: 'timesheetValue',
      title: messages['common.timesheetValue'],
      dataIndex: 'timesheetValue',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'hours',
      title: messages['common.hours'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) => props?.payeeUmbrellaValue?.totalHours || '-',
    },
    {
      key: 'margin',
      title: messages['common.margin'],
      align: 'right',
      sortDirections: ['descend', 'ascend'],
      render: (data) =>
        includes(
          ['PAYEE', 'UMBRELLA', 'HYBRID', 'PAYE_HYBRID', 'PAYE_FTH'],
          data?.payrollRef?.paymentModel,
        )
          ? formatMoney(
              data?.payrollRef?.payeeUmbrellaValue?.payslipMargin || 0,
              showCurrencyByCountry(payrollCountry),
            )
          : formatMoney(
              data?.margin || 0,
              showCurrencyByCountry(payrollCountry),
            ),
    },
    {
      key: 'payrollRef',
      title: messages['common.apprenticeLevy'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) => (
        <span>
          {formatMoney(
            props?.payeeUmbrellaValue?.appLevy || 0,
            showCurrencyByCountry(payrollCountry),
          )}
        </span>
      ),
    },
    {
      key: 'salary',
      title: messages['common.salary'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(props?.salary || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'holidayPay',
      title: messages['common.advancedHolidayPay'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.holidayPay || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'expenseClaimed',
      title: messages['common.expensesClaimed'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.expenseClaimed || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'expensesPaid',
      title: messages['common.expensesPaid'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.expensesPaid || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'bonus',
      title: messages['common.bonus'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.bonus || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'otherPayment',
      title: messages['common.otherPayment'],
      align: 'right',
      dataIndex: 'otherPayment',
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'otherDeduction',
      title: messages['common.otherDeduction'],
      align: 'right',
      dataIndex: 'otherDeduction',
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'totalPayment',
      title: messages['common.totalPayment'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.totalPayment || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'tax',
      title: messages['common.taxDeducted'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.tax || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'employeeNI',
      title: messages['common.employeeNI'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.employeeNI || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'employersNI',
      title: messages['common.employerNI'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.employersNI || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'employeePension',
      title: messages['common.employeePension'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          getShownEmployeePension(props),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'eePensionDeductionMethod',
      title: messages['common.employeeDeductionMethod'],
      dataIndex: 'payrollRef',
      render: (props) => {
        const method = props?.payeeUmbrellaValue?.eePensionDeductionMethod;
        return (
          pensionDeductionMethods.find((item) => item.value === method)
            ?.label || '-'
        );
      },
      width: 250,
    },
    {
      key: 'employersPension',
      title: messages['common.employerPension'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          getShownEmployersPension(props),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'erPensionDeductionMethod',
      title: messages['common.employerDeductionMethod'],
      dataIndex: 'payrollRef',
      render: (props) => {
        const method = props?.payeeUmbrellaValue?.erPensionDeductionMethod;
        return (
          pensionDeductionMethods.find((item) => item.value === method)
            ?.label || '-'
        );
      },
      width: 250,
    },
    {
      key: 'holidayPayRetained',
      title: messages['common.holidayPayRetained'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.retainedHolidayPay || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'holidayPaid',
      title: messages['common.holidayPaid'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          (props?.holidayPaid || 0) -
            (props?.payeeUmbrellaValue?.holidayPay || 0),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalDeduction',
      title: messages['common.totalDeduction'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.totalDeduction || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'netPay',
      title: messages['common.netPay'],
      align: 'right',
      dataIndex: 'netPay',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'studentLoan',
      title: messages['common.studentLoan'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.studentLoan || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'postgraduateLoan',
      title: messages['common.pgStudentLoan'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.postgraduateLoan || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'attachmentOrderDeducted',
      title: messages['common.attachment'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.attachmentOrderDeducted || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'slickPay',
      title: messages['common.sickPaySSP'],
      align: 'right',
      dataIndex: 'slickPay',
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'maternityPay',
      title: messages['common.maternityPaySMP'],
      align: 'right',
      dataIndex: 'maternityPay',
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'neonatalPay',
      title: messages['common.neonatalCarePaySNCP'],
      align: 'right',
      dataIndex: 'neonatalPay',
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
      width: 250,
    },
    {
      key: 'adoptionPay',
      title: messages['common.adoptionPaySAP'],
      align: 'right',
      dataIndex: 'adoptionPay',
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'paternityPay',
      title: messages['common.paternityPaySPP'],
      align: 'right',
      dataIndex: 'paternityPay',
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'additionalSPP',
      title: messages['common.additionalSPP'],
      align: 'right',
      dataIndex: 'additionalSPP',
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'grossForTaxYTD',
      title: messages['common.grossForTaxYTD'],
      align: 'right',
      render: (dp) =>
        formatMoney(
          (dp?.payrollRef?.payeeUmbrellaValue?.grossForTaxTD || 0) +
            (dp?.payrollRef?.payeeUmbrellaValue?.p45GrossToDate || 0) +
            (dp?.payrollRef?.payeeUmbrellaValue?.p11?.grossForTax || 0),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'taxYTD',
      title: messages['common.taxYTD'],
      align: 'right',
      render: (dp) =>
        formatMoney(
          (dp?.payrollRef?.payeeUmbrellaValue?.taxTD || 0) +
            (dp?.payrollRef?.payeeUmbrellaValue?.p45TaxToDate || 0) +
            (dp?.payrollRef?.payeeUmbrellaValue?.p11?.tax || 0),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'paymentDate',
      title: messages['common.paymentDate'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (values) =>
        values?.paymentDate
          ? moment(values.paymentDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'paid',
      title: messages['common.paid'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (values) =>
        formatMoney(values?.paid || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'outstanding',
      title: messages['common.outstanding'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (value) =>
        formatMoney(
          value?.outstanding || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'fpsAmount',
      title: messages['common.fpsAmount'],
      align: 'right',
      dataIndex: 'fpsAmount',
      render: (props) =>
        formatMoney(props || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'grossForTax',
      title: messages['common.grossForTax'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.grossForTax || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'grossForNI',
      title: messages['common.grossForNI'],
      align: 'right',
      dataIndex: 'payrollRef',
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.grossForTaxAndNI || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
  ];

  const irelandColumns: ColumnsType<IPayslip> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => handleDownloadPayslip(record.id)}>
            <Tooltip title="Download">
              <span>
                <Icons.IconDownload />
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'id',
      title: messages['common.payslipsId'],
      fixed: 'left',
      dataIndex: 'id',
      width: 200,
      render: (value) => value?.replace('SFB', 'PAY') || '_',
    },
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'],
      align: 'right',
      width: 200,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        `${value?.taxPeriod || ''}/${
          value?.taxYear || value?.currentYear || ''
        }`,
    },
    {
      key: 'companyRef',
      title: messages['common.employer'],
      dataIndex: 'companyRef',
      width: 350,
      render: (values) => values?.companyName || '_',
    },
    {
      key: 'timesheetValue',
      title: messages['common.grossPay'],
      dataIndex: 'timesheetValue',
      align: 'right',
      width: 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'hours',
      title: messages['common.units'],
      align: 'right',
      dataIndex: 'payrollRef',
      width: 250,
      render: (props) =>
        props?.totalHours ||
        props?._totalHours ||
        '-',
    },
    {
      key: 'salary',
      title: messages['common.salary'],
      dataIndex: 'timesheetValue',
      align: 'right',
      width: 250,
      render: (value) =>
        formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'otherPayment',
      title: messages['common.otherPayment'],
      dataIndex: 'otherPayment',
      align: 'right',
      width: 250,
      render: (value) =>
        formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'otherDeduction',
      title: messages['common.otherDeduction'],
      dataIndex: 'otherDeduction',
      align: 'right',
      width: 250,
      render: (value) =>
        formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'totalPayment',
      title: messages['common.totalPayment'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.totalPayment || props?._totalPayment || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'grossForTax',
      title: messages['common.taxableGross'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.irelandValue?.taxGross ||
            props?.payeeUmbrellaValue?.grossForTax ||
            0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'taxDeducted',
      title: messages['common.taxDeducted'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.irelandValue?.tax || props?.payeeUmbrellaValue?.tax || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'uscGross',
      title: messages['common.uscAbleGross'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.uscGross || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'usc',
      title: messages['common.usc'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(props?.usc || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'eePrsiGross',
      title: messages['common.employeePrsiAbleGross'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.eePrsiGross || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'eePRSI',
      title: messages['common.employeePrsi'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(props?.eePRSI || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'erPrsiGross',
      title: messages['common.employerPrsiAbleGross'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.erPrsiGross || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'erPRSI',
      title: messages['common.employerPrsi'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(props?.erPRSI || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'pensionGross',
      title: messages['common.mffGross'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.pensionGross || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'eePension',
      title: messages['common.employeeMFF'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.eePension || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'erPension',
      title: messages['common.employerMFF'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.erPension || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'lptDeducted',
      title: messages['common.lpt'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.lptDeducted || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'netPay',
      title: messages['common.netPay'],
      dataIndex: 'netPay',
      align: 'right',
      width: 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'advancedHolidayPay',
      title: messages['common.advancedHolidayPay'],
      dataIndex: ['payrollRef', 'irelandValue'],
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.holidayPayAdvanced || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'holidayPayRetained',
      title: messages['common.holidayPayRetained'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.retainedHolidayPay || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'holidayPaid',
      title: messages['common.holidayPaid'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          (props?.holidayPaid || 0) -
            (props?.irelandValue?.holidayPayAdvanced || 0),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'totalDeduction',
      title: messages['common.totalDeduction'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          getIrelandTotalDeduction(props),
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'ssp',
      title: messages['common.sickPaySSP'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (props) =>
        formatMoney(
          props?.payeeUmbrellaValue?.ssp || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
    {
      key: 'paymentDate',
      title: messages['common.paymentDate'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (values) =>
        values?.paymentDate
          ? moment(values.paymentDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'paid',
      title: messages['common.paid'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (values) =>
        formatMoney(values?.paid || 0, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'outstanding',
      title: messages['common.outstanding'],
      dataIndex: 'payrollRef',
      align: 'right',
      width: 250,
      render: (values) =>
        formatMoney(
          values?.outstanding || 0,
          showCurrencyByCountry(payrollCountry),
        ),
    },
  ];

  const columns: ColumnsType<IPayslip> =
    payrollCountry === IRELAND ? irelandColumns : defaultColumns;

  const handleDownloadPayslip = async (id: string) => {
    try {
      const res = await downloadPayslipEmployee(id);
      if (res.success) {
        window.open(res.data, '_blank');
      } else {
        // NX-5866: the backend answers a refusal with HTTP 200 + success:false,
        // so the api hook's error toast never fires. Without this branch the
        // button silently does nothing at all.
        showMessage({
          type: 'error',
          content: res?.error
            ? getErrorMessage(res.error)
            : messages['message.somethingWentWrong'],
        });
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {isCIS ? messages['common.selfBill'] : messages['common.payslip']}
      </Typography.Title>
      {isDownloadPayslipPending && <AppLoader />}
      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={handleSearchChange} />
        <TaxYearSelect
          initValue={currentTaxWeekTaxYear.taxYear}
          onFilter={(value) =>
            handleFetchData({ ...queryParams, taxYear: value.taxYear })
          }
        />
      </div>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 10000, y: 700 }}
          titleEmpty={messages['payslip.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
        />
      </AppsContent>
    </div>
  );
};

export default Payslip;
